import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
} from "react";
import BandPreview from "./BandPreview";
import ReaderPreview from "./ReaderPreview";
import InteractTV from "./InteractTV";


/* ============================================================
   DesignStudioTV — remote-driven sibling of DesignStudio.
   Same wizard, same onPrepare/onTransmit/onCancel contract; the
   ONLY differences from touch are navigation + TV sizing. All
   band-firing logic lives in the parent (MagicBandTV) exactly as
   it does in touch (MagicBand owns it, DesignStudio calls up).

   Focus model: one onKeyDown on the wizard root. It owns every
   zone INSIDE the wizard and stopPropagation()s anything it
   handles so the parent's tab handler never sees it. The two
   edges that cross the file boundary:
     • Up off Step-1 mode 0 (or Interact top row) → onExitTop()
       (parent focuses the tab row).
     • Down off the bottom-most control → we DON'T preventDefault,
       so native focus drops to the bottom bar, same as every
       other Display screen.

   Idle guard: the READY screen ("Ready To Send The Magic?") is
   reached via openOverlay → onPrepare(), which starts the parent's
   WAKE_UP_FAST loop. If the user walks away there, that loop would
   run forever. A 30s idle timer tears down exactly like Go Back
   (onCancel() stops the loop + sends cancel, then closeOverlay()).
   Any remote activity on that screen resets the 30s.
   ============================================================ */

const COLORS = [
  { name: "red", hex: "#FF3B30", label: "Red" },
  { name: "orange", hex: "#FF9500", label: "Orange" },
  { name: "yellow", hex: "#FFD60A", label: "Yellow" },
  { name: "green", hex: "#30D158", label: "Green" },
  { name: "cyan", hex: "#64D2FF", label: "Cyan" },
  { name: "blue", hex: "#0A84FF", label: "Blue" },
  { name: "purple", hex: "#BF5AF2", label: "Purple" },
  { name: "pink", hex: "#FF4FC8", label: "Pink" },
  { name: "white", hex: "#F2F2F7", label: "White" },
  { name: "off", hex: "#1C1C1E", label: "Off" },
];
const SWATCH_COLS = 5;
const HEX = COLORS.reduce((m, c) => ((m[c.name] = c.hex), m), {});

const MODES = [
  { key: "1color", label: "ONE" },
  { key: "2color", label: "TWO" },
  { key: "5color", label: "THREE+" },
  { key: "interact", label: "INTERACT" },
];

const MODE_DEFAULTS = {
  "1color": { outer: "cyan", inner: "cyan" },
  "2color": { outer: "blue", inner: "yellow" },
  "5color": { tl: "red", tr: "blue", c: "yellow", bl: "green", br: "purple" },
};

// Guided zone order per mode (1color is single — no sequence).
const SEQ = {
  "2color": ["outer", "inner"],
  "5color": ["tl", "tr", "c", "bl", "br"],
};

const ZONE_LABELS = {
  outer: "Outer Ring",
  inner: "Inner Circle",
  tl: "Top Left",
  tr: "Top Right",
  c: "Center",
  bl: "Bottom Left",
  br: "Bottom Right",
};

const INTERACT_ACTIONS = [
  { key: "ropedrop", icon: "🌅", label: "ROPE DROP", cls: "ds-act-ropedrop" },
  { key: "dvc", icon: "🏰", label: "DVC", cls: "ds-act-dvc" },
  { key: "noentry", icon: "🚫", label: "NO ENTRY", cls: "ds-act-noentry" },
  { key: "alert", icon: "🚨", label: "ALERT", cls: "ds-act-alert" },
];
const INTERACT_COLS = 2;

const LOADING_DURATION_MS = 4000;
const TRANSMIT_DURATION = 18; // countdown seconds
const READY_IDLE_MS = 30000; // walk-away guard on the "ready" screen

const OK_KEYS = new Set(["Enter", " "]);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

const DesignStudioTV = forwardRef(
    ({ onPrepare, onTransmit, onCancel, onExitTop, device }, ref) => {
    // ----- wizard state -----
    const [step, setStep] = useState("mode"); // "mode" | "pick" | "interact"
    const [mode, setMode] = useState(null);
    const [colors, setColors] = useState({
      outer: "blue",
      inner: "yellow",
      tl: "red",
      tr: "blue",
      c: "yellow",
      bl: "green",
      br: "purple",
    });
    const [seqIndex, setSeqIndex] = useState(0);
    const [stageView, setStageView] = useState("band"); // "band" | "reader"
    const [interactAnim, setInteractAnim] = useState(null);

    // overlay: "hidden" | "loading" | "ready" | "transmitting"
    const [overlayState, setOverlayState] = useState("hidden");
    const [countdown, setCountdown] = useState(TRANSMIT_DURATION);

    // ----- focus state -----
    // zone: "modes" | "swatches" | "actions" | "interact" | "interactBack"
    const [focusZone, setFocusZone] = useState("modes");
    const [modeIndex, setModeIndex] = useState(0);
    const [swatchIndex, setSwatchIndex] = useState(0);
    const [actionIndex, setActionIndex] = useState(0); // 0 Back, 1 Next
    const [interactIndex, setInteractIndex] = useState(0);
    const [readyIndex, setReadyIndex] = useState(0); // 0 Send, 1 Go Back

    const modeRefs = useRef([]);
    const swatchRefs = useRef([]);
    const actionRefs = useRef([]); // [Back, Next]
    const interactRefs = useRef([]);
    const interactBackRef = useRef(null);
    const readyRefs = useRef([]); // [Send Now, Go Back]
    const txRef = useRef(null); // Already illuminated
    const allOffBtnRef = useRef(null); // Bands OFF button (TWO / THREE+)
    const allOffRef = useRef(false);   // true = this send is Bands OFF
    const interactRef = useRef(null); // InteractTV (focusEntry handle)

    const loadingTimerRef = useRef(null);
    const tickTimerRef = useRef(null);
    const idleTimerRef = useRef(null); // 30s walk-away guard on "ready"

    const seq = SEQ[mode] || [];
    const isGuided = seq.length > 0;
    const target = isGuided ? seq[seqIndex] : "outer";

    // ---------- focus helpers (set state AND move DOM focus) ----------
    const soon = (fn) => setTimeout(fn, 60);

    const focusMode = (i) => {
      const n = clamp(i, 0, MODES.length - 1);
      setFocusZone("modes");
      setModeIndex(n);
      modeRefs.current[n]?.focus();
    };
    const focusSwatch = (i) => {
      const n = clamp(i, 0, COLORS.length - 1);
      setFocusZone("swatches");
      setSwatchIndex(n);
      swatchRefs.current[n]?.focus();
    };
    const focusAction = (i) => {
      const n = clamp(i, 0, 1);
      setFocusZone("actions");
      setActionIndex(n);
      actionRefs.current[n]?.focus();
    };
    const focusInteract = (i) => {
      const n = clamp(i, 0, INTERACT_ACTIONS.length - 1);
      setFocusZone("interact");
      setInteractIndex(n);
      interactRefs.current[n]?.focus();
    };
    const focusInteractBack = () => {
      setFocusZone("interactBack");
      interactBackRef.current?.focus();
    };
        const focusAllOff = () => {
      setFocusZone("alloff");
      allOffBtnRef.current?.focus();
    };
      const focusReady = (i) => {
      const n = clamp(i, 0, 1);
      setReadyIndex(n);
      readyRefs.current[n]?.focus();
    };

    // ---------- step transitions ----------
    const goToMode = () => {
      setStep("mode");
      setMode(null);
      setStageView("band");
      setInteractAnim(null);
      soon(() => focusMode(0));
    };

    const handleModeOk = (modeKey) => {
            if (modeKey === "interact") {
        // InteractTV takes over via the early return below; it focuses itself.
        setMode("interact");
        setStep("interact");
        return;
      }
      setMode(modeKey);
      setColors((prev) => ({ ...prev, ...MODE_DEFAULTS[modeKey] }));
      setSeqIndex(0);
      setStep("pick");
      setStageView("band");
      soon(() => focusSwatch(0));
    };

    const handleSwatchOk = (colorName) => {
      const s = SEQ[mode] || [];
      if (s.length === 0) {
        // single colour — applies to whole band, no advance
        setColors((prev) => ({ ...prev, outer: colorName, inner: colorName }));
        return;
      }
      const t = s[seqIndex];
      setColors((prev) => ({ ...prev, [t]: colorName }));
      if (seqIndex < s.length - 1) {
        // advance to next zone; grid stays focused, prompt updates
        setSeqIndex((i) => i + 1);
      } else {
        // last zone done → land on Next so they consciously confirm
        soon(() => focusAction(1));
      }
    };
    // "Bands OFF" (TWO / THREE+ only): stays in this menu, but sends the same
    // proven single-color Off command as ONE → Off → Next.
    const handleAllOff = () => {
      allOffRef.current = true;
      openOverlay();
    };
    const handleInteractAction = (key) => {
      // re-trigger even if same key (clear → set), drives ReaderPreview
      setInteractAnim(null);
      requestAnimationFrame(() => setInteractAnim(key));
    };

    // ---------- overlay flow (mirrors touch DesignStudio) ----------
    const openOverlay = () => {
      if (onPrepare) onPrepare(); // parent starts the WAKE_UP_FAST loop
      setOverlayState("loading");
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = setTimeout(() => {
        setOverlayState((s) => (s === "loading" ? "ready" : s));
      }, LOADING_DURATION_MS);
    };

    const handleSend = async () => {
  if (overlayState !== "ready") return;
  clearTimeout(idleTimerRef.current); // sending — kill the walk-away guard
  // Ask the parent FIRST. "sent" = we have control → run the countdown.
  // "pending" = take-control modal is up → hide, NO countdown; Yes →
  // parent calls beginTransmitCountdown(); No → we're back at selection.
  // "failed" = claim failed → back to selection, nothing sent.
    // Bands OFF → send single-color Off without changing the on-screen design.
  const sendArgs = allOffRef.current
    ? { mode: "1color", colors: { ...colors, outer: "off", inner: "off" } }
    : { mode, colors };
  const verdict = onTransmit ? await onTransmit(sendArgs) : "sent";
  if (verdict === "sent") {
    setCountdown(TRANSMIT_DURATION);
    setOverlayState("transmitting");
  } else {
    closeOverlay();
  }
};

    const closeOverlay = () => {
      clearTimeout(loadingTimerRef.current);
      clearTimeout(tickTimerRef.current);
      clearTimeout(idleTimerRef.current);
      setOverlayState("hidden");
      allOffRef.current = false; // next Next sends the real design again
      // restart the guided walk from the first zone (Outer Ring / Top Left)
      setSeqIndex(0);
      soon(() => focusSwatch(0)); // land back on the colour grid at zone 1
    };

    const handleIlluminated = () => {
      if (onCancel) onCancel();
      closeOverlay();
    };
    const handleOverlayBack = () => {
      if (onCancel) onCancel();
      closeOverlay();
    };

    // Arm / re-arm the 30s walk-away guard on the READY screen. Called when
    // ready first appears AND on any remote activity there, so an actively
    // present user never gets torn down mid-decision.
    const armReadyIdle = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        // identical teardown to Go Back: stop the wake loop, send cancel, close
        if (onCancel) onCancel();
        closeOverlay();
      }, READY_IDLE_MS);
    };

    // focus the primary button when each overlay phase appears
    useEffect(() => {
      if (overlayState === "ready") {
        soon(() => focusReady(0));
        armReadyIdle(); // start the walk-away guard
      }
      if (overlayState === "transmitting") soon(() => txRef.current?.focus());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayState]);

    // countdown driver
    useEffect(() => {
      if (overlayState !== "transmitting") return;
      if (countdown <= 0) {
        closeOverlay();
        return;
      }
      tickTimerRef.current = setTimeout(
        () => setCountdown((s) => s - 1),
        1000
      );
      return () => clearTimeout(tickTimerRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayState, countdown]);

    // mount: auto-focus ONE
    useEffect(() => {
      soon(() => focusMode(0));
      return () => {
        clearTimeout(loadingTimerRef.current);
        clearTimeout(tickTimerRef.current);
        clearTimeout(idleTimerRef.current);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // parent (tab row) hands focus back into the wizard via this
    useImperativeHandle(
      ref,
      () => ({
        focusEntry: () => {
          if (overlayState !== "hidden") return; // don't yank focus mid-overlay
          if (step === "mode") focusMode(0);
          else if (step === "pick")
            focusZone === "actions"
              ? focusAction(actionIndex)
              : focusSwatch(swatchIndex);
                    else if (step === "interact") interactRef.current?.focusEntry?.();
        },
        beginTransmitCountdown: () => {
      setCountdown(TRANSMIT_DURATION);
      setOverlayState("transmitting");
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [step, focusZone, overlayState, actionIndex, swatchIndex, interactIndex]
    );

    // ---------------------- KEY HANDLER ----------------------
    const handleKey = (e) => {
      const k = e.key;

      // ----- OVERLAYS take priority -----
      if (overlayState === "loading") {
        // inert; swallow so native focus can't wander
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (overlayState === "ready") {
        e.stopPropagation();
        armReadyIdle(); // any activity resets the 30s walk-away guard
        if (k === "ArrowDown") {
          e.preventDefault();
          focusReady(1);
          return;
        }
        if (k === "ArrowUp") {
          e.preventDefault();
          focusReady(0);
          return;
        }
        if (k === "ArrowLeft" || k === "ArrowRight") {
          e.preventDefault();
          return;
        }
        if (OK_KEYS.has(k)) {
          e.preventDefault();
          readyIndex === 0 ? handleSend() : handleOverlayBack();
          return;
        }
        if (k === "Escape") {
          e.preventDefault();
          handleOverlayBack();
          return;
        }
        return;
      }
      if (overlayState === "transmitting") {
        e.stopPropagation();
        if (OK_KEYS.has(k)) {
          e.preventDefault();
          handleIlluminated();
          return;
        }
        if (
          k === "ArrowUp" ||
          k === "ArrowDown" ||
          k === "ArrowLeft" ||
          k === "ArrowRight"
        ) {
          e.preventDefault();
          return;
        }
        if (k === "Escape") {
          e.preventDefault();
          handleIlluminated();
          return;
        }
        return;
      }

      // ----- STEP 1: modes -----
      if (step === "mode") {
        if (k === "ArrowDown") {
          if (modeIndex === MODES.length - 1) return; // off INTERACT → bar (native)
          e.preventDefault();
          e.stopPropagation();
          focusMode(modeIndex + 1);
          return;
        }
        if (k === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          if (modeIndex === 0) onExitTop?.(); // up off ONE → tab row
          else focusMode(modeIndex - 1);
          return;
        }
        if (k === "ArrowLeft" || k === "ArrowRight") {
          e.preventDefault();
          e.stopPropagation();
          return; // dead — never lose vertical focus
        }
        if (OK_KEYS.has(k)) {
          e.preventDefault();
          e.stopPropagation();
          handleModeOk(MODES[modeIndex].key);
          return;
        }
        return;
      }

      // ----- STEP 2: pick -----
      if (step === "pick") {
        if (focusZone === "swatches") {
          const row = Math.floor(swatchIndex / SWATCH_COLS);
          const lastRow = Math.floor((COLORS.length - 1) / SWATCH_COLS);
          if (k === "ArrowLeft") {
            e.preventDefault();
            e.stopPropagation();
            if (swatchIndex % SWATCH_COLS !== 0) focusSwatch(swatchIndex - 1);
            return;
          }
          if (k === "ArrowRight") {
            e.preventDefault();
            e.stopPropagation();
            if (
              swatchIndex % SWATCH_COLS !== SWATCH_COLS - 1 &&
              swatchIndex < COLORS.length - 1
            )
              focusSwatch(swatchIndex + 1);
            return;
          }
          if (k === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            if (row > 0) focusSwatch(swatchIndex - SWATCH_COLS);
            else onExitTop?.(); // top row → tab row (same as Step 1)
            return;
          }
          if (k === "ArrowDown") {
            e.preventDefault();
            e.stopPropagation();
            if (row < lastRow)
              focusSwatch(Math.min(swatchIndex + SWATCH_COLS, COLORS.length - 1));
            else if (isGuided) focusAllOff(); // Bands OFF
            else focusAction(1); // Next
            return;
          }
          if (OK_KEYS.has(k)) {
            e.preventDefault();
            e.stopPropagation();
            handleSwatchOk(COLORS[swatchIndex].name);
            return;
          }
          if (k === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            goToMode();
            return;
          }
          return;
        }
        
                if (focusZone === "alloff") {
          if (k === "ArrowUp") { e.preventDefault(); e.stopPropagation(); focusSwatch(swatchIndex); return; }
          if (k === "ArrowDown") { e.preventDefault(); e.stopPropagation(); focusAction(1); return; }
          if (k === "ArrowLeft" || k === "ArrowRight") { e.preventDefault(); e.stopPropagation(); return; }
          if (OK_KEYS.has(k)) { e.preventDefault(); e.stopPropagation(); handleAllOff(); return; }
          if (k === "Escape") { e.preventDefault(); e.stopPropagation(); goToMode(); return; }
          return;
        }
        if (focusZone === "actions") {
          if (k === "ArrowLeft") {
            e.preventDefault();
            e.stopPropagation();
            focusAction(0);
            return;
          }
          if (k === "ArrowRight") {
            e.preventDefault();
            e.stopPropagation();
            focusAction(1);
            return;
          }
          if (k === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            if (isGuided) focusAllOff(); else focusSwatch(swatchIndex);
            return;
          }
          if (k === "ArrowDown") return; // → bar (native)
          if (OK_KEYS.has(k)) {
            e.preventDefault();
            e.stopPropagation();
            actionIndex === 0 ? goToMode() : openOverlay();
            return;
          }
          if (k === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            goToMode();
            return;
          }
          return;
        }
      }

      // ----- INTERACT -----
      if (step === "interact") {
        if (focusZone === "interact") {
          if (k === "ArrowLeft") {
            e.preventDefault();
            e.stopPropagation();
            if (interactIndex % INTERACT_COLS !== 0)
              focusInteract(interactIndex - 1);
            return;
          }
          if (k === "ArrowRight") {
            e.preventDefault();
            e.stopPropagation();
            if (interactIndex % INTERACT_COLS !== INTERACT_COLS - 1)
              focusInteract(interactIndex + 1);
            return;
          }
          if (k === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            if (interactIndex >= INTERACT_COLS)
              focusInteract(interactIndex - INTERACT_COLS);
            else onExitTop?.(); // top row → tab row
            return;
          }
          if (k === "ArrowDown") {
            e.preventDefault();
            e.stopPropagation();
            if (interactIndex < INTERACT_COLS)
              focusInteract(interactIndex + INTERACT_COLS);
            else focusInteractBack();
            return;
          }
          if (OK_KEYS.has(k)) {
            e.preventDefault();
            e.stopPropagation();
            handleInteractAction(INTERACT_ACTIONS[interactIndex].key);
            return;
          }
          if (k === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            goToMode();
            return;
          }
          return;
        }
        if (focusZone === "interactBack") {
          if (k === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            focusInteract(INTERACT_COLS); // back up into bottom row
            return;
          }
          if (k === "ArrowDown") return; // → bar (native)
          if (k === "ArrowLeft" || k === "ArrowRight") {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (OK_KEYS.has(k)) {
            e.preventDefault();
            e.stopPropagation();
            goToMode();
            return;
          }
          if (k === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            goToMode();
            return;
          }
          return;
        }
      }
    };

    // ---------------------- RENDER ----------------------
        // INTERACT is a full takeover, same as the display's DesignStudio:
    // InteractTV owns its own keys/focus. Safe here — all hooks are above.
    if (step === "interact") {
      return (
        <InteractTV
          ref={interactRef}
          onBack={goToMode}
          onExitTop={onExitTop}
          device={device}
        />
      );
    }
    const renderModeStep = () => (
      <>
        <div className="dstv-tag">STEP 1</div>
        <h1 className="dstv-title">How Many Colors?</h1>
        <div className="dstv-modes">
          {MODES.map((m, i) => (
            <button
              key={m.key}
              ref={(el) => (modeRefs.current[i] = el)}
              className={`dstv-mode ${
                m.key === "interact" ? "dstv-mode-interact" : ""
              }`}
              tabIndex={0}
              onFocus={() => {
                setFocusZone("modes");
                setModeIndex(i);
              }}
                            onClick={() => handleModeOk(m.key)}
            >
              {m.key === "interact" ? (
                <span className="dstv-interact-label">{m.label}</span>
              ) : (
                m.label
              )}
            </button>
          ))}
        </div>
      </>
    );

    const renderPickStep = () => {
      const selectedName = isGuided ? colors[target] : colors.outer;
      const prompt = isGuided
        ? `${ZONE_LABELS[target]} — Pick Your Color`
        : "Pick Your Color";
      return (
        <>
          <div className="dstv-tag">STEP 2</div>
          <h1 className="dstv-title">{prompt}</h1>

          {isGuided && (
            <div className="dstv-zones">
              {seq.map((z, i) => (
                <div
                  key={z}
                  className={`dstv-zone ${i === seqIndex ? "current" : ""}`}
                >
                  <span
                    className="dstv-zone-dot"
                    style={{ background: HEX[colors[z]] }}
                  />
                  <span className="dstv-zone-lbl">{ZONE_LABELS[z]}</span>
                </div>
              ))}
            </div>
          )}

          <div className="dstv-swatches">
            {COLORS.map((c, i) => (
              <button
                key={c.name}
                ref={(el) => (swatchRefs.current[i] = el)}
                className={`dstv-swatch ${c.name === "off" ? "off" : ""} ${
                  selectedName === c.name ? "selected" : ""
                }`}
                style={{ background: c.hex }}
                tabIndex={0}
                title={c.label}
                onFocus={() => {
                  setFocusZone("swatches");
                  setSwatchIndex(i);
                }}
                onClick={() => handleSwatchOk(c.name)}
              >
                <span className="dstv-swatch-lbl">{c.label}</span>
              </button>
            ))}
          </div>
                    {isGuided && (
            <button
              ref={allOffBtnRef}
              className="dstv-btn dstv-btn-alloff"
              tabIndex={0}
              onFocus={() => setFocusZone("alloff")}
              onClick={() => handleAllOff()}
            >
              Bands OFF
            </button>
          )}

          <div className="dstv-actions">
            <button
              ref={(el) => (actionRefs.current[0] = el)}
              className="dstv-btn dstv-btn-back"
              tabIndex={0}
              onFocus={() => {
                setFocusZone("actions");
                setActionIndex(0);
              }}
              onClick={() => goToMode()}
            >
              Back
            </button>
            <button
              ref={(el) => (actionRefs.current[1] = el)}
              className="dstv-btn dstv-btn-next"
              tabIndex={0}
              onFocus={() => {
                setFocusZone("actions");
                setActionIndex(1);
              }}
              onClick={() => openOverlay()}
            >
              Next
            </button>
          </div>
        </>
      );
    };

    const renderInteractStep = () => (
      <>
        <div className="dstv-tag">INTERACT</div>
        <h1 className="dstv-title">Pick an Animation</h1>
        <div className="dstv-interact-grid">
          {INTERACT_ACTIONS.map((a, i) => (
            <button
              key={a.key}
              ref={(el) => (interactRefs.current[i] = el)}
              className={`dstv-interact ${a.cls} ${
                interactAnim === a.key ? "active" : ""
              }`}
              tabIndex={0}
              onFocus={() => {
                setFocusZone("interact");
                setInteractIndex(i);
              }}
              onClick={() => handleInteractAction(a.key)}
            >
              <span className="dstv-interact-icon">{a.icon}</span>
              <span className="dstv-interact-text">{a.label}</span>
            </button>
          ))}
        </div>
        <div className="dstv-actions">
          <button
            ref={interactBackRef}
            className="dstv-btn dstv-btn-back dstv-btn-wide"
            tabIndex={0}
            onFocus={() => setFocusZone("interactBack")}
            onClick={() => goToMode()}
          >
            Back
          </button>
        </div>
      </>
    );

    const renderOverlay = () => {
      if (overlayState === "hidden") return null;

      if (overlayState === "loading") {
        return (
          <div className="dstv-ov dstv-ov-loading">
            <div className="dstv-spinner" />
            <div className="dstv-ov-load-text">
              Loading Your Design
              <span className="dstv-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </div>
          </div>
        );
      }

      if (overlayState === "ready") {
        return (
          <div className="dstv-ov dstv-ov-ready">
            <h2 className="dstv-ov-heading">
              Ready To Send
              <br />
              The Magic?
            </h2>
            <button
              ref={(el) => (readyRefs.current[0] = el)}
              className="dstv-send-btn"
              tabIndex={0}
              onFocus={() => setReadyIndex(0)}
              onClick={() => handleSend()}
            >
              SEND NOW!
            </button>
            <button
              ref={(el) => (readyRefs.current[1] = el)}
              className="dstv-goback-btn"
              tabIndex={0}
              onFocus={() => setReadyIndex(1)}
              onClick={() => handleOverlayBack()}
            >
              Go Back
            </button>
          </div>
        );
      }

      // transmitting
      return (
        <div className="dstv-ov dstv-ov-tx">
          <div className="dstv-rings">
            <span />
            <span />
            <span />
            <div className="dstv-timer">{countdown}</div>
          </div>
          <h2 className="dstv-ov-heading sm">Beacon transmitting…</h2>
          <p className="dstv-ov-body">
            May take up to 20 seconds for all bands to respond
          </p>
          <button
            ref={txRef}
            className="dstv-illum-btn"
            tabIndex={0}
            onClick={() => handleIlluminated()}
          >
            Already illuminated?
            <span className="dstv-illum-sub">Tap OK to pick another color!</span>
          </button>
        </div>
      );
    };

    return (
      <div className="dstv-designer" onKeyDown={handleKey}>
        <style>{`

          /* band-preview classes (inlined so we don't import DesignStudio.css,
             which collapses our TV grid) */
          .ds-band-container{position:relative;height:100%;aspect-ratio:860/1500;max-height:100%;}
          .ds-band-image{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;z-index:2;pointer-events:none;}
          .ds-band-layer{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1;}
          .ds-band-shine{z-index:3;}
          .ds-band-interact{z-index:5;pointer-events:auto;}
          .ds-band-hit{cursor:pointer;fill:transparent;}
          .dstv-designer{display:grid;grid-template-columns:1.05fr 0.95fr;gap:28px;
            width:100%;height:100%;min-height:0;font-family:"Lato",sans-serif;color:#1a2542;position:relative;}

          .dstv-panel{background:#fff;border-radius:28px;padding:34px 40px;display:flex;
            flex-direction:column;box-shadow:0 10px 34px rgba(20,30,60,.08);min-height:0;overflow:hidden;}

          .dstv-tag{align-self:flex-start;background:#007bff;color:#fff;font-size:22px;
            font-weight:900;letter-spacing:3px;padding:8px 18px;border-radius:8px;margin-bottom:14px;}
          .dstv-title{font-size:44px;font-weight:900;color:#1a2542;margin:0 0 22px;
            letter-spacing:-.5px;line-height:1.05;}

          /* Step 1 modes */
          .dstv-modes{display:flex;flex-direction:column;gap:16px;flex:1;justify-content:center;}
          .dstv-mode{height:88px;border-radius:18px;border:4px solid #007bff;background:#fff;
            font-family:"Lato",sans-serif;font-size:40px;font-weight:900;letter-spacing:5px;color:#1a2542;
            cursor:pointer;outline:none;display:flex;align-items:center;justify-content:center;
            transition:transform .12s ease, box-shadow .12s ease, background .12s ease, color .12s ease;}
          .dstv-mode:focus{background:#007bff;color:#fff;border-color:#05f651;
            box-shadow:0 0 0 5px #05f651,0 0 34px rgba(5,246,81,.55);transform:scale(1.03);}
                    /* INTERACT — ported from DesignStudioOverlay.css (.ds-mode-btn-interact):
             navy base, rainbow text, animated rainbow edge, pulsing glow. */
          .dstv-mode-interact{border:none;background:#1a2452;color:#fff;position:relative;
            overflow:hidden;isolation:isolate;}
          .dstv-interact-label{position:relative;z-index:2;
            background:linear-gradient(90deg,#ff5e62,#ff9966,#ffd76e,#6effa1,#64d2ff,#bf5af2,#ff5e62);
            background-size:300% 100%;
            -webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;
            animation:dstv-rainbow-text 6s linear infinite;font-weight:900;letter-spacing:5px;}
          .dstv-mode-interact::before{content:"";position:absolute;inset:0;padding:4px;border-radius:18px;
            background:linear-gradient(120deg,#ff5e62,#ffd76e,#6effa1,#64d2ff,#bf5af2,#ff5e62);
            background-size:300% 300%;
            -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
            -webkit-mask-composite:xor;mask-composite:exclude;
            animation:dstv-rainbow-border 6s linear infinite;z-index:1;}
          .dstv-mode-interact::after{content:"";position:absolute;inset:0;border-radius:18px;
            box-shadow:0 0 22px rgba(140,120,255,.45);
            animation:dstv-interact-glow 3s ease-in-out infinite;z-index:0;pointer-events:none;}
          .dstv-mode-interact:focus{background:#223066;color:#fff;border:none;
            box-shadow:0 0 0 5px #05f651,0 0 34px rgba(5,246,81,.55);transform:scale(1.03);}
          @keyframes dstv-rainbow-border{0%{background-position:0% 50%;}100%{background-position:300% 50%;}}
          @keyframes dstv-rainbow-text{0%{background-position:0% 50%;}100%{background-position:300% 50%;}}
          @keyframes dstv-interact-glow{0%,100%{box-shadow:0 0 16px rgba(140,120,255,.3);}50%{box-shadow:0 0 30px rgba(140,120,255,.6);}}

          /* guided zone progress chips */
          .dstv-zones{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:18px;}
          .dstv-zone{display:flex;align-items:center;gap:8px;padding:8px 14px;border-radius:12px;
            background:#f1f3f7;border:3px solid transparent;font-size:20px;font-weight:800;color:#46506b;}
          .dstv-zone-lbl{color:#46506b;}
          .dstv-zone.current{background:#eaf2ff;border-color:#007bff;color:#1a2542;transform:scale(1.04);}
          .dstv-zone-dot{width:22px;height:22px;border-radius:50%;border:2px solid rgba(0,0,0,.18);
            box-shadow:inset 0 0 0 1px rgba(255,255,255,.4);}

          /* swatch grid (big TV tiles) */
          .dstv-swatches{display:grid;grid-template-columns:repeat(${SWATCH_COLS},1fr);gap:16px;
            flex:1;min-height:0;margin-bottom:18px;}
          .dstv-swatch{position:relative;border-radius:18px;border:5px solid transparent;cursor:pointer;
            outline:none;box-shadow:inset 0 0 0 1px rgba(20,30,60,.10);min-height:0;
            transition:transform .12s ease, border-color .12s ease, box-shadow .12s ease;}
          .dstv-swatch.off{box-shadow:inset 0 0 0 1px #4b5563;}
          .dstv-swatch:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);
            transform:scale(1.06);z-index:2;}
          .dstv-swatch.selected{border-color:#1a2542;}
          .dstv-swatch.selected:focus{border-color:#05f651;}
          .dstv-swatch.selected::after{content:'✓';position:absolute;top:50%;left:50%;
            transform:translate(-50%,-50%);color:#fff;font-weight:900;font-size:46px;
            text-shadow:0 1px 6px rgba(0,0,0,.45);}
          .dstv-swatch-lbl{position:absolute;left:10px;bottom:8px;background:rgba(255,255,255,.9);
            color:#222;font-size:16px;font-weight:800;padding:2px 10px;border-radius:8px;pointer-events:none;}

          /* Back / Next */
          .dstv-actions{display:flex;gap:16px;margin-top:auto;}
          .dstv-btn{height:84px;border-radius:16px;font-family:"Lato",sans-serif;font-size:30px;
            font-weight:900;letter-spacing:1px;cursor:pointer;outline:none;border:4px solid transparent;
            display:flex;align-items:center;justify-content:center;
            transition:transform .12s ease, box-shadow .12s ease, background .12s ease, color .12s ease;}
          .dstv-btn:focus{box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);transform:scale(1.03);}
          .dstv-btn-back{flex:0 0 240px;background:#fff;color:#1a2542;border-color:#d8dce3;}
          .dstv-btn-back:focus{border-color:#05f651;}
          .dstv-btn-wide{flex:1;}
          .dstv-btn-next{flex:1;background:#007bff;color:#fff;text-transform:uppercase;letter-spacing:2px;}
          .dstv-btn-next:focus{background:#0066d6;border-color:#05f651;}
          .dstv-btn-alloff{width:100%;height:68px;flex:0 0 auto;margin:6px 0 24px;background:#4B5563;color:#fff;letter-spacing:2px;text-transform:uppercase;}
          .dstv-btn-alloff:focus{background:#5b6472;border-color:#05f651;}

          /* Interact grid */
          .dstv-interact-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px;flex:1;
            align-content:center;min-height:0;margin-bottom:18px;}
          .dstv-interact{position:relative;border-radius:18px;border:4px solid #2a335a;
            background:#11162a;color:#fff;font-family:"Lato",sans-serif;cursor:pointer;outline:none;
            display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;min-height:130px;
            transition:transform .12s ease, box-shadow .12s ease;}
          .dstv-interact:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);
            transform:scale(1.03);}
          .dstv-interact.active{outline:3px solid rgba(255,255,255,.6);}
          .dstv-interact-icon{font-size:46px;line-height:1;}
          .dstv-interact-text{font-size:26px;font-weight:900;letter-spacing:2px;}

          /* Band stage */
          .dstv-band-stage{background:#fff;border-radius:28px;display:flex;align-items:center;
            justify-content:center;overflow:hidden;box-shadow:0 10px 34px rgba(20,30,60,.08);
            min-height:0;padding:18px;position:relative;}
          .dstv-left-locked{pointer-events:none;filter:grayscale(.85) brightness(.55);transition:filter .3s ease;}

          /* Overlays */
          .dstv-ov{position:absolute;inset:0;z-index:200;display:flex;flex-direction:column;
            align-items:center;justify-content:center;border-radius:28px;text-align:center;
            background:rgba(10,14,28,.86);padding:30px;}
          .dstv-ov-loading{background:rgba(10,14,28,.5);padding-top:7vh;}
          .dstv-ov-heading{font-size:48px;font-weight:900;color:#fff;margin:0 0 30px;line-height:1.1;
            text-shadow:0 4px 24px rgba(0,0,0,.5);}
          .dstv-ov-heading.sm{font-size:36px;margin:0 0 10px;}
          .dstv-ov-body{font-size:24px;color:#cdd3e4;margin:0 0 28px;max-width:520px;}

          .dstv-spinner{width:160px;height:160px;border-radius:50%;border:10px solid rgba(255,255,255,.15);
            border-top-color:#f4d76e;animation:dstv-spin 1s linear infinite;margin-bottom:26px;
            filter:drop-shadow(0 0 10px rgba(244,215,110,.5));}
          @keyframes dstv-spin{to{transform:rotate(360deg);}}
          .dstv-ov-load-text{color:#fff;font-size:30px;font-weight:900;letter-spacing:.5px;}
          .dstv-dots>span{display:inline-block;opacity:.3;animation:dstv-dot 1.4s ease-in-out infinite;}
          .dstv-dots>span:nth-child(2){animation-delay:.2s;}
          .dstv-dots>span:nth-child(3){animation-delay:.4s;}
          @keyframes dstv-dot{0%,80%,100%{opacity:.3;}40%{opacity:1;}}

          .dstv-send-btn{padding:26px 70px;border-radius:999px;border:5px solid transparent;
            background:radial-gradient(circle at 35% 30%,#fff3c4 0%,#f4d76e 35%,#d4a843 75%,#a37a1f 100%);
            color:#1a2542;font-family:"Lato",sans-serif;font-size:38px;font-weight:900;letter-spacing:2px;
            text-transform:uppercase;cursor:pointer;outline:none;margin-bottom:22px;
            box-shadow:0 16px 44px rgba(212,168,67,.5);transition:transform .15s ease, box-shadow .2s ease;}
          .dstv-send-btn:focus{transform:scale(1.06);box-shadow:0 0 0 6px #05f651,0 0 40px rgba(5,246,81,.6);}
          .dstv-goback-btn{padding:16px 46px;border-radius:999px;border:4px solid transparent;
            background:rgba(255,255,255,.14);color:#e6e6ee;font-family:"Lato",sans-serif;font-size:24px;
            font-weight:800;letter-spacing:1px;text-transform:uppercase;cursor:pointer;outline:none;
            transition:transform .12s ease, box-shadow .12s ease, background .15s ease;}
          .dstv-goback-btn:focus{background:rgba(255,255,255,.22);box-shadow:0 0 0 5px #05f651;transform:scale(1.04);}

          .dstv-rings{position:relative;width:190px;height:190px;margin:0 auto 28px;display:flex;
            align-items:center;justify-content:center;}
          .dstv-rings>span{position:absolute;inset:0;border-radius:50%;border:4px solid rgba(100,210,255,.6);
            animation:dstv-ring 2.4s ease-out infinite;}
          .dstv-rings>span:nth-child(2){animation-delay:.8s;}
          .dstv-rings>span:nth-child(3){animation-delay:1.6s;}
          @keyframes dstv-ring{0%{transform:scale(.35);opacity:.9;}100%{transform:scale(1.15);opacity:0;}}
          .dstv-timer{position:relative;z-index:2;width:96px;height:96px;display:flex;align-items:center;
            justify-content:center;font-size:48px;font-weight:800;color:#64d2ff;border-radius:50%;
            border:4px solid rgba(100,210,255,.5);box-shadow:0 0 20px rgba(100,210,255,.4);
            background:rgba(10,14,28,.6);font-variant-numeric:tabular-nums;}
          .dstv-illum-btn{max-width:520px;padding:18px 36px;border-radius:18px;border:4px solid rgba(100,210,255,.4);
            background:rgba(100,210,255,.08);color:#64d2ff;font-family:"Lato",sans-serif;font-size:24px;
            font-weight:800;line-height:1.4;cursor:pointer;outline:none;
            transition:transform .12s ease, box-shadow .12s ease, background .15s ease;}
          .dstv-illum-btn:focus{background:rgba(100,210,255,.16);border-color:#05f651;
            box-shadow:0 0 0 5px #05f651;transform:scale(1.03);}
          .dstv-illum-sub{display:block;font-size:18px;font-weight:600;opacity:.85;margin-top:6px;}
        `}</style>

        {/* LEFT — control panel (locked visually while transmitting) */}
        <div
          className={`dstv-panel ${
            overlayState === "transmitting" ? "dstv-left-locked" : ""
          }`}
        >
          {step === "mode" && renderModeStep()}
          {step === "pick" && renderPickStep()}
          {step === "interact" && renderInteractStep()}
        </div>

        {/* RIGHT — band stage + overlay (reused presentation) */}
        <div className="dstv-band-stage">
          {stageView === "reader" ? (
            <ReaderPreview animation={interactAnim} />
          ) : (
            <BandPreview mode={mode} colors={colors} onZoneTap={() => {}} />
          )}
          {stageView === "band" && renderOverlay()}
        </div>
      </div>
    );
  }
);

export default DesignStudioTV;
